import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import { PButton, PButtonPure, PText } from "@porsche-design-system/components-react"
import { breakpoint, spacingFluidSm, spacingStaticLg } from "@porsche-design-system/components-react/emotion"
import type { InventoryVehicleIdIdentifier, OfferExposeSection } from "@slfinrtl/ui-bff-routes"
import { createStockVehicleExposeDocumentRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { ButtonGroup, Checkbox, FormLayout, Input } from "src/components"
import { trackEvent } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useBffDynamic } from "src/hooks/useBffDynamic/useBffDynamic"
import { useConfig } from "src/hooks/useConfig/useConfig"
import { ExposeLanguageSelection } from "src/modals/createExposeModal/ExposeLanguageSelection"
import { createExposeModalMessages } from "src/modals/createExposeModal/createExposeModal.messages"
import {
	TRACK_CREATE_VEHICLE_EXPOSE,
	TRACK_OPEN_CREATE_VEHICLE_EXPOSE,
} from "src/modals/createExposeModal/exposeTracking"
import { getInitialExposeSections } from "src/modals/createExposeModal/getInitialExposeSections"
import { useCap } from "src/screens/offers/common/useCap"
import { useOfferVehicleData } from "src/screens/offers/common/useOfferVehicleData"
import { createStockExposeModalMessages } from "./createStockExposeModal.messages"

export interface CreateStockExposeModalContainerProps {
	vehicleKey: string
}

type Props = ModalProps<CreateStockExposeModalContainerProps>

export const CreateStockExposeModalContainer = withErrorHandler(({ id, modalProps }: Props) => {
	const { vehicleKey } = modalProps
	const { hideModal } = useContext(ModalContext)
	const { formatMessage } = useIntl()
	const { availableLocales, locale } = useDealerContext()
	const {
		offers: { exposeTyreLabelsAvailable },
	} = useConfig()

	const [selectedSections, setSelectedSections] = useState<OfferExposeSection[]>(() =>
		getInitialExposeSections(exposeTyreLabelsAvailable),
	)
	const [customerName, setCustomerName] = useState("")
	const [selectedLocale, setSelectedLocale] = useState<Locale>(
		availableLocales.find((item) => item === locale) ?? availableLocales[0],
	)
	const [isCreating, setIsCreating] = useState(false)
	const [hasCapError, setHasCapError] = useState(false)

	const createDocument = useBffDynamic(createStockVehicleExposeDocumentRoute, "inline")
	const hasMultipleLanguages = availableLocales.length > 1

	const vehicleIdentifier: InventoryVehicleIdIdentifier = useMemo(
		() => ({ type: "INVENTORY_VEHICLE_ID", vehicleId: vehicleKey }),
		[vehicleKey],
	)

	const { view: vehicleDataView } = useOfferVehicleData({ vehicleIdentifier })

	const { isCapInitialized, isCustomerNameLoading, selectCustomerFromCap } = useCap({
		onCustomerSelect: ({ name }) => {
			setCustomerName(name)
			setHasCapError(false)
		},
		onError: () => setHasCapError(true),
		orderTypeCode: vehicleDataView?.orderTypeCode,
	})

	const hide = useCallback(() => hideModal(id), [hideModal, id])

	useEffect(() => {
		trackEvent(TRACK_OPEN_CREATE_VEHICLE_EXPOSE)
	}, [])

	const toggleSection = useCallback((section: OfferExposeSection, checked: boolean) => {
		setSelectedSections((prev) => (checked ? [...prev, section] : prev.filter((s) => s !== section)))
	}, [])

	const onSubmit = useCallback(() => {
		trackEvent(TRACK_CREATE_VEHICLE_EXPOSE)
		setIsCreating(true)
		createDocument({
			body: { customerName, locale: selectedLocale, sections: selectedSections },
			pathParams: { inventoryId: vehicleKey },
			queryParams: {},
		})
			.then(({ downloadUrl }) => {
				window.open(downloadUrl, "_blank")
				hide()
			})
			.catch(() => {
				// error display is handled by useBffDynamic's inline notification
			})
			.finally(() => setIsCreating(false))
	}, [createDocument, customerName, selectedLocale, selectedSections, vehicleKey, hide])

	return (
		<Modal id={id} heading={formatMessage(createStockExposeModalMessages.headline)}>
			<Container>
				<CustomerSection>
					<Input
						name="customerName"
						type="text"
						label={`${formatMessage(createStockExposeModalMessages.customerNameLabel)} (${formatMessage(formMessages.optional)})`}
						value={customerName}
						onChange={(value) => setCustomerName(value ?? "")}
						data-testid="create-stock-expose-customer-name-input"
					/>
					{isCapInitialized && (
						<PButtonPure
							icon="plus"
							loading={isCustomerNameLoading}
							onClick={selectCustomerFromCap}
							data-testid="create-stock-expose-select-cap-customer-button"
						>
							<FormattedMessage {...createStockExposeModalMessages.selectFromCap} />
						</PButtonPure>
					)}
					{hasCapError && (
						<PText color="error">
							<FormattedMessage {...createStockExposeModalMessages.capError} />
						</PText>
					)}
				</CustomerSection>

				<FormLayout>
					<PText>
						<FormattedMessage {...createStockExposeModalMessages.subtitle} />
					</PText>
					<Checkbox
						name="vehicleImages"
						checked={selectedSections.includes("vehicleImages")}
						onChange={(checked) => toggleSection("vehicleImages", checked)}
					>
						<FormattedMessage {...createStockExposeModalMessages.sectionVehicleImages} />
					</Checkbox>
					<Checkbox
						name="specialEquipment"
						checked={selectedSections.includes("specialEquipment")}
						onChange={(checked) => toggleSection("specialEquipment", checked)}
					>
						<FormattedMessage {...createStockExposeModalMessages.sectionSpecialEquipment} />
					</Checkbox>
					<Checkbox
						name="standardEquipment"
						checked={selectedSections.includes("standardEquipment")}
						onChange={(checked) => toggleSection("standardEquipment", checked)}
					>
						<FormattedMessage {...createStockExposeModalMessages.sectionStandardEquipment} />
					</Checkbox>
					<Checkbox
						name="technicalData"
						checked={selectedSections.includes("technicalData")}
						onChange={(checked) => toggleSection("technicalData", checked)}
					>
						<FormattedMessage {...createStockExposeModalMessages.sectionTechnicalData} />
					</Checkbox>
					{exposeTyreLabelsAvailable && (
						<Checkbox
							name="tyreLabels"
							checked={selectedSections.includes("tyreLabels")}
							onChange={(checked) => toggleSection("tyreLabels", checked)}
						>
							<FormattedMessage {...createStockExposeModalMessages.sectionTyreLabels} />
						</Checkbox>
					)}
				</FormLayout>

				{hasMultipleLanguages && (
					<ExposeLanguageSelection
						selectedLocale={selectedLocale}
						onLocaleChange={setSelectedLocale}
						subheadline={createExposeModalMessages.languageSubheadline}
					/>
				)}

				<ErrorNotificationInline dismissButton={false} />

				<ButtonGroup withoutMarginTop>
					<PButton
						loading={isCreating}
						disabled={selectedSections.length === 0}
						onClick={onSubmit}
						data-testid="create-stock-expose-modal-create-button"
					>
						<FormattedMessage {...createStockExposeModalMessages.create} />
					</PButton>
					<PButton
						type="button"
						variant="secondary"
						disabled={isCreating}
						onClick={hide}
						data-testid="create-stock-expose-modal-cancel-button"
					>
						<FormattedMessage {...createStockExposeModalMessages.cancel} />
					</PButton>
				</ButtonGroup>
			</Container>
		</Modal>
	)
})

const Container = styled.div`
    display: flex;
	flex-direction: column;
	gap: ${spacingStaticLg};

	@media (min-width: ${breakpoint.s}px) {
        min-width: 564px;
    }
`

const CustomerSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingFluidSm};
`
